/**
 * RMAI backend client for the labelling plugin.
 *
 * All requests carry the `X-RMAI-API-Key` header (see config.ts). A
 * missing key is thrown from every call so the UI can render a
 * "configure your API key" hint centrally.
 *
 * Errors are normalised to `RmaiError` with an HTTP status and a
 * human-readable message. The caller decides how to surface them.
 */

import { getRmaiApiKey, getRmaiBaseUrl } from './config';
import type {
  Algorithm,
  Annotation,
  AnnotationRequest,
  ImageRegistration,
} from './types';

export class RmaiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly detail?: string,
  ) {
    super(message);
    this.name = 'RmaiError';
  }
}

export class RmaiApiKeyMissingError extends RmaiError {
  constructor() {
    super(
      'RMAI API key is not set. In the browser console run: ' +
        `localStorage.setItem('rmai-api-key', '<your-key>')`,
      0,
    );
    this.name = 'RmaiApiKeyMissingError';
  }
}

function auth(): Record<string, string> {
  const key = getRmaiApiKey();
  if (!key) throw new RmaiApiKeyMissingError();
  return { 'X-RMAI-API-Key': key };
}

async function parseError(response: Response): Promise<RmaiError> {
  let detail: string | undefined;
  try {
    const body = await response.json();
    detail = typeof body?.detail === 'string' ? body.detail : JSON.stringify(body);
  } catch {
    detail = (await response.text()) || undefined;
  }
  const summary =
    response.status === 401
      ? 'RMAI rejected the API key (401)'
      : response.status === 404
        ? 'RMAI resource not found (404)'
        : `RMAI request failed (${response.status})`;
  return new RmaiError(`${summary}${detail ? `: ${detail}` : ''}`, response.status, detail);
}

async function jsonGet<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
  const url = new URL(`${getRmaiBaseUrl()}${path}`);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
    }
  }
  const response = await fetch(url.toString(), { headers: auth() });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as T;
}

async function jsonPost<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${getRmaiBaseUrl()}${path}`, {
    method: 'POST',
    headers: { ...auth(), 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as T;
}

// --------------------------------------------------------------------
// Algorithms
// --------------------------------------------------------------------

export async function listAlgorithms(): Promise<Algorithm[]> {
  const body = await jsonGet<{ algorithms: Algorithm[] }>('/labelling/algorithms');
  return body.algorithms;
}

// --------------------------------------------------------------------
// Images
// --------------------------------------------------------------------

/**
 * Register a DICOM file with RMAI so it becomes referenceable by
 * imageId. Idempotent: RMAI dedupes on SOPInstanceUID, so re-uploading
 * the same DICOM returns the same imageId — safe to call on every
 * wizard open without caching.
 *
 * Uses XMLHttpRequest so we can report upload progress via
 * ``onProgress`` — needed because the DICOM can be several MB and
 * over a slow residential uplink the fetch would otherwise appear
 * to hang for tens of seconds with no feedback.
 */
export async function registerImage(
  dicomBytes: Blob,
  onProgress?: (uploaded: number, total: number) => void,
): Promise<ImageRegistration> {
  const key = getRmaiApiKey();
  if (!key) throw new RmaiApiKeyMissingError();
  const form = new FormData();
  form.append('file', dicomBytes, 'image.dcm');
  const url = `${getRmaiBaseUrl()}/images`;

  return new Promise<ImageRegistration>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('X-RMAI-API-Key', key);
    xhr.responseType = 'text';
    xhr.upload.onprogress = (e) => {
      if (onProgress && e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onerror = () => reject(new RmaiError('network error posting DICOM to RMAI', 0));
    xhr.ontimeout = () => reject(new RmaiError('DICOM upload timed out', 0));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as ImageRegistration);
        } catch (e) {
          reject(new RmaiError(`RMAI returned invalid JSON: ${(e as Error).message}`, xhr.status));
        }
      } else {
        let detail: string | undefined;
        try {
          detail = (JSON.parse(xhr.responseText) as { detail?: string }).detail;
        } catch {
          detail = xhr.responseText;
        }
        reject(new RmaiError(`RMAI request failed (${xhr.status})${detail ? `: ${detail}` : ''}`, xhr.status, detail));
      }
    };
    // 90-second timeout — a slow uplink at 500 kbps can push ~5 MB in ~90s.
    xhr.timeout = 90_000;
    xhr.send(form);
  });
}

// --------------------------------------------------------------------
// Annotations
// --------------------------------------------------------------------

export async function upsertAnnotation(request: AnnotationRequest): Promise<Annotation> {
  return jsonPost<Annotation>('/annotations', request);
}

/**
 * Fetch the annotation matching (imageId, algorithm, reader) if one
 * exists. Returns null when there is no prior annotation — the wizard
 * uses this to decide between "start fresh" and "resume".
 */
export async function findAnnotation(
  imageId: string,
  algorithm: string,
  reader: string,
): Promise<Annotation | null> {
  const body = await jsonGet<{ annotations: Annotation[] }>('/annotations', {
    imageId,
    algorithm,
    reader,
  });
  return body.annotations[0] ?? null;
}

export async function deleteAnnotation(id: string): Promise<void> {
  const response = await fetch(`${getRmaiBaseUrl()}/annotations/${id}`, {
    method: 'DELETE',
    headers: auth(),
  });
  if (!response.ok) throw await parseError(response);
}
